// Subtasks section for the task drawer: lists the task's direct children with a
// completed/total summary and a quick-add input. New subtasks inherit the
// parent's project and are created via POST /api/tasks with parent_id.

import { useEffect, useState, type FormEvent } from "react";
import { createActionItem, createTask, updateTask } from "../lib/api";
import { useMutation } from "../lib/useApi";
import type { TaskRow } from "../lib/types";
import { Alert, Spinner } from "./States";

const isDone = (t: TaskRow) => (t.status_code ?? t.status) === "done";

// The Action Items container isn't addressable via the generic task-create path
// (no project members; items are private) — its subtasks go to /api/action-items.
// Mirrors ACTION_ITEMS_PROJECT_ID in @ac/tenant-db.
const ACTION_ITEMS_PROJECT_ID = "sys_action_items";

export function TaskSubtasks({
  parentId,
  projectId,
  initial,
  onChanged,
  readOnly = false,
}: {
  parentId: string;
  projectId: string;
  initial: TaskRow[];
  onChanged: () => void;
  /** 0090: read-only shared viewer — no toggling or adding subtasks. */
  readOnly?: boolean;
}) {
  const [subtasks, setSubtasks] = useState<TaskRow[]>(initial);
  const [title, setTitle] = useState("");
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const { run, pending, error } = useMutation((vars: { title: string }) =>
    projectId === ACTION_ITEMS_PROJECT_ID
      ? createActionItem({ title: vars.title, parent_id: parentId })
      : createTask({ project_id: projectId, title: vars.title, parent_id: parentId }),
  );

  useEffect(() => {
    setSubtasks(initial);
  }, [initial, parentId]);

  const done = subtasks.filter(isDone).length;

  async function toggle(s: TaskRow) {
    if (readOnly) return;
    setTogglingId(s.id);
    try {
      await updateTask(s.id, { status_code: isDone(s) ? "todo" : "done" });
      onChanged();
    } catch {
      /* surface nothing inline; the drawer reload reflects the source of truth */
    } finally {
      setTogglingId(null);
    }
  }

  async function add(e: FormEvent) {
    e.preventDefault();
    const t = title.trim();
    if (!t) return;
    const res = await run({ title: t });
    if (res) {
      setTitle("");
      onChanged();
    }
  }

  return (
    <div className="task-subtasks">
      <h3 className="drawer-section-title">
        Subtasks {subtasks.length ? <span className="faint">· {done}/{subtasks.length}</span> : null}
      </h3>

      {subtasks.length > 0 ? (
        <>
          <div className="progress-track" style={{ marginBottom: 8 }}>
            <div
              className={`progress-fill${done === subtasks.length ? " done" : ""}`}
              style={{ width: `${Math.round((done / subtasks.length) * 100)}%` }}
            />
          </div>
          <ul className="subtask-list">
            {subtasks.map((s) => (
              <li key={s.id} className="subtask">
                <button
                  type="button"
                  className={`subtask-check${isDone(s) ? " done" : ""}`}
                  role="checkbox"
                  aria-checked={isDone(s)}
                  aria-label={`Mark "${s.title}" ${isDone(s) ? "not done" : "done"}`}
                  disabled={readOnly || togglingId === s.id}
                  onClick={() => toggle(s)}
                >
                  {isDone(s) ? "✓" : ""}
                </button>
                <span className={`subtask-title${isDone(s) ? " done" : ""}`}>{s.title}</span>
                <span className="faint mono">#{s.number}</span>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {error ? <Alert kind="error">{error}</Alert> : null}
      {readOnly ? null : (
      <form className="subtask-add" onSubmit={add}>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          aria-label="Add a subtask"
          placeholder="Add a subtask…"
        />
        <button className="btn small" type="submit" disabled={pending || !title.trim()}>
          {pending ? <Spinner label="Adding…" /> : "Add"}
        </button>
      </form>
      )}
    </div>
  );
}
